/** The beneficiaries behind one operator's row on the Operators tab.
 *
 *  Renders the SAME `LeadCard` the campaign's beneficiary list uses.
 *  Reported 2026-09-27 — "it shows in an odd way; please use the same
 *  card as the beneficiary listing." It was a bespoke inline list of
 *  name + status pill + nested session links, which meant two different
 *  renderings of one thing and no way to act on a row.
 *
 *  Paginated for the same reason the picker was: the endpoint used to
 *  answer with every row at once, and the busiest operator on the
 *  campaign that prompted this holds 484 of them.
 */
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import type { CampaignMembership, Paginated } from "@/lib/types";

import { LeadCard } from "./CampaignLeads";

export function OperatorBeneficiaries({
  campaignId,
  userId,
}: {
  campaignId: string;
  userId: number;
}) {
  const { t } = useTranslation();
  const rows = useInfiniteQuery({
    queryKey: ["campaign-operator-beneficiaries", campaignId, userId],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) =>
      (
        await api.get<Paginated<CampaignMembership>>(
          ApiPaths.campaignOperatorBeneficiaries(campaignId, userId),
          { params: { page: String(pageParam) } },
        )
      ).data,
    getNextPageParam: (last, _all, lastParam) =>
      last.next ? (lastParam as number) + 1 : undefined,
    retry: false,
  });

  const leads = (rows.data?.pages ?? []).flatMap((p) => p.results ?? []);
  const total = rows.data?.pages[0]?.count ?? 0;

  if (rows.isLoading) {
    return <p className="text-xs text-nb-text-3">{t("common.loading")}</p>;
  }
  if (rows.isError) {
    return (
      <p className="text-xs text-cvp-danger">
        {t("campaigns.detail.noBeneficiaries")}
      </p>
    );
  }
  if (leads.length === 0) {
    return (
      <p className="text-xs text-nb-text-3">
        {t("campaigns.detail.noBeneficiaries")}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <ul className="space-y-2">
        {leads.map((lead) => (
          <LeadCard
            key={lead.id}
            lead={lead}
            campaignId={campaignId}
            // The drawer belongs to the Beneficiaries tab; from here the
            // card is a read surface, so opening it is a no-op rather
            // than a second, half-wired detail view.
            onOpen={() => {}}
          />
        ))}
      </ul>

      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-nb-text-3">
          {t("leads.bulkAttach.showing", { shown: leads.length, total })}
        </span>
        {rows.hasNextPage && (
          <button
            type="button"
            className="btn-ghost text-xs"
            disabled={rows.isFetchingNextPage}
            onClick={() => void rows.fetchNextPage()}
          >
            {rows.isFetchingNextPage
              ? t("common.loading")
              : t("leads.bulkAttach.loadMore", "Load more")}
          </button>
        )}
      </div>
    </div>
  );
}
